import { createLocalEvent, draftToEvent } from './upcomingEvents';

const MAX_WEEKLY_OCCURRENCES = 260;
const DEFAULT_END_DATE = '2027-12-31';
// Dad Partners launches with a special, theme-less call on this Sunday.
export const LAUNCH_DATE = '2027-03-14';
export const LAUNCH_CALL_TITLE = 'Launch Call';

export const WEEKDAY_OPTIONS = [
  'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday',
].map((label, value) => ({ label, value }));

const pad = (value) => String(value).padStart(2, '0');
const formatLocalDate = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const parseLocalDate = (value) => {
  const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const [, year, month, day] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day), 12);
  return formatLocalDate(date) === value ? date : null;
};

const getNthWeekdayDate = (year, monthIndex, dayOfWeek, occurrence) => {
  const first = new Date(year, monthIndex, 1, 12);
  const day = 1 + ((dayOfWeek - first.getDay() + 7) % 7) + ((occurrence - 1) * 7);
  return formatLocalDate(new Date(year, monthIndex, day, 12));
};

const getEasterDate = (year) => {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = ((19 * a) + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + (2 * e) + (2 * i) - h - k) % 7;
  const m = Math.floor((a + (11 * h) + (22 * l)) / 451);
  const month = Math.floor((h + l - (7 * m) + 114) / 31);
  const day = ((h + l - (7 * m) + 114) % 31) + 1;
  return `${year}-${pad(month)}-${pad(day)}`;
};

// Themed Sundays: theme is decided by which Sunday of the month it is.
// A holiday that removes a themed Sunday removes that month's theme too.
export const SUNDAY_THEMES = ['parenting', 'marriage', 'work', 'spiritual_growth'];

export const getSundayTheme = (dateValue) => {
  const date = parseLocalDate(dateValue);
  if (!date || date.getDay() !== 0) return null;
  return SUNDAY_THEMES[Math.floor((date.getDate() - 1) / 7)] || null;
};

const getLastWeekdayDate = (year, monthIndex, dayOfWeek) => {
  const last = new Date(year, monthIndex + 1, 0, 12);
  last.setDate(last.getDate() - ((last.getDay() - dayOfWeek + 7) % 7));
  return formatLocalDate(last);
};

// The Sunday that falls Dec 26-31, or null in years when none does.
const getSundayAfterChristmas = (year) => {
  for (let day = 26; day <= 31; day += 1) {
    const value = `${year}-12-${pad(day)}`;
    if (parseLocalDate(value).getDay() === 0) return value;
  }
  return null;
};

const shiftDate = (dateValue, days) => {
  const date = parseLocalDate(dateValue);
  date.setDate(date.getDate() + days);
  return formatLocalDate(date);
};

// Add or remove entries here without changing weekly generation logic.
// Each entry must have exactly one of monthDay or getDate(year).
export const HOLIDAYS_TO_SKIP = [
  { id: 'new-years-day', label: "New Year's Day", monthDay: '01-01', control: 'holidays' },
  { id: 'easter-sunday', label: 'Easter Sunday', getDate: getEasterDate, control: 'holidays' },
  { id: 'mothers-day', label: "Mother's Day", getDate: (year) => getNthWeekdayDate(year, 4, 0, 2), control: 'mothersDay' },
  { id: 'fathers-day', label: "Father's Day", getDate: (year) => getNthWeekdayDate(year, 5, 0, 3), control: 'fathersDay' },
  { id: 'independence-day', label: 'Independence Day', monthDay: '07-04', control: 'holidays' },
  { id: 'christmas-eve', label: 'Christmas Eve', monthDay: '12-24', control: 'holidays' },
  { id: 'christmas-day', label: 'Christmas Day', monthDay: '12-25', control: 'holidays' },
  { id: 'new-years-eve', label: "New Year's Eve", monthDay: '12-31', control: 'holidays' },
  { id: 'presidents-day-weekend', label: "Presidents' Day weekend", getDate: (year) => shiftDate(getNthWeekdayDate(year, 1, 1, 3), -1), control: 'holidays' },
  { id: 'sunday-after-christmas', label: 'Sunday after Christmas', getDate: getSundayAfterChristmas, control: 'holidays' },
  { id: 'memorial-day-weekend', label: 'Memorial Day weekend', getDate: (year) => shiftDate(getLastWeekdayDate(year, 4, 1), -1), control: 'holidays' },
  { id: 'labor-day-weekend', label: 'Labor Day weekend', getDate: (year) => shiftDate(getNthWeekdayDate(year, 8, 1, 1), -1), control: 'holidays' },
  { id: 'thanksgiving-weekend', label: 'Thanksgiving weekend', getDate: (year) => shiftDate(getNthWeekdayDate(year, 10, 4, 4), 3), control: 'holidays' },
];

export const getNextWeekdayDate = (dayOfWeek = 0, now = new Date()) => {
  const next = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12);
  next.setDate(next.getDate() + ((Number(dayOfWeek) - next.getDay() + 7) % 7));
  return formatLocalDate(next);
};

export const createWeeklySeriesForm = (now = new Date()) => {
  const nextSunday = getNextWeekdayDate(0, now);
  const startDate = nextSunday < LAUNCH_DATE ? LAUNCH_DATE : nextSunday;
  return {
    launchDate: LAUNCH_DATE,
    titlePattern: '',
    dayOfWeek: 0,
    startDate,
    endDate: startDate <= DEFAULT_END_DATE ? DEFAULT_END_DATE : startDate,
    startTime: '',
    endTime: '',
    locationLabel: '',
    link: '',
    description: '',
    assignThemes: true,
    skipFifthOccurrence: true,
    skipHolidays: true,
    skipMothersDay: true,
    skipFathersDay: true,
    customSkipText: '',
  };
};

export const parseCustomSkipDates = (rawValue) => {
  const values = String(rawValue || '').split(/[\n,]/).map((value) => value.trim()).filter(Boolean);
  const dates = [];
  const invalidValues = [];
  values.forEach((value) => {
    if (!parseLocalDate(value)) invalidValues.push(value);
    else if (!dates.includes(value)) dates.push(value);
  });
  return { dates, invalidValues };
};

const issue = (code, message, values) => ({ code, message, ...(values ? { values } : {}) });

const getStaticIssues = (options) => {
  const issues = [];
  const start = parseLocalDate(options.startDate);
  const end = parseLocalDate(options.endDate);
  const dayOfWeek = Number(options.dayOfWeek);
  const timePattern = /^([01]\d|2[0-3]):[0-5]\d$/;
  if (!String(options.titlePattern || '').trim()) issues.push(issue('INVALID_OPTIONS', 'Add a title pattern.'));
  if (!start) issues.push(issue('INVALID_OPTIONS', 'Choose a valid start date.'));
  if (!end) issues.push(issue('INVALID_OPTIONS', 'Choose a valid end date.'));
  if (!Number.isInteger(dayOfWeek) || dayOfWeek < 0 || dayOfWeek > 6) issues.push(issue('INVALID_OPTIONS', 'Choose a valid day of week.'));
  if (!timePattern.test(String(options.startTime || ''))) issues.push(issue('INVALID_OPTIONS', 'Choose a valid start time.'));
  if (options.endTime && !timePattern.test(options.endTime)) issues.push(issue('INVALID_OPTIONS', 'Choose a valid end time or leave it blank.'));
  if (start && end && end < start) issues.push(issue('INVALID_RANGE', 'End date cannot be before start date.'));
  if (options.endTime && timePattern.test(options.endTime) && timePattern.test(options.startTime) && options.endTime < options.startTime) {
    issues.push(issue('INVALID_RANGE', 'End time cannot be before start time.'));
  }
  if (String(options.link || '').trim()) {
    try {
      const parsed = new URL(options.link);
      if (!['http:', 'https:'].includes(parsed.protocol)) throw new Error('invalid protocol');
    } catch (_) {
      issues.push(issue('INVALID_OPTIONS', 'Event links must be valid http or https URLs.'));
    }
  }
  return { issues, start, end, dayOfWeek };
};

const holidayDate = (entry, year) => entry.monthDay ? `${year}-${entry.monthDay}` : entry.getDate(year);
const weekdayName = (dayOfWeek) => WEEKDAY_OPTIONS[dayOfWeek]?.label || 'selected weekday';

const planCandidates = (options) => {
  const { issues, start, end, dayOfWeek } = getStaticIssues(options);
  const emptySummary = {
    candidateCount: 0,
    includedDates: [],
    skippedDates: [],
    skippedCounts: { custom: 0, mothersDay: 0, fathersDay: 0, holiday: 0, fifthOccurrence: 0 },
  };
  if (issues.length) return { summary: emptySummary, issues };

  const first = new Date(start);
  first.setDate(first.getDate() + ((dayOfWeek - first.getDay() + 7) % 7));
  const candidates = [];
  for (const date = new Date(first); date <= end; date.setDate(date.getDate() + 7)) {
    candidates.push(formatLocalDate(date));
    if (candidates.length > MAX_WEEKLY_OCCURRENCES) {
      return {
        summary: { ...emptySummary, candidateCount: candidates.length },
        issues: [issue('TOO_MANY_OCCURRENCES', 'Choose a date range with 260 or fewer weekly occurrences.')],
      };
    }
  }
  if (candidates.length === 0) {
    return {
      summary: emptySummary,
      issues: [issue('NO_OCCURRENCES', `No ${weekdayName(dayOfWeek)} falls within this date range.`)],
    };
  }

  const custom = new Set(options.customSkipDates || []);
  const skippedDates = [];
  const includedDates = [];
  const skippedCounts = { ...emptySummary.skippedCounts };
  candidates.forEach((dateValue) => {
    const date = parseLocalDate(dateValue);
    const enabledHoliday = HOLIDAYS_TO_SKIP.find((entry) => {
      const enabled = entry.control === 'holidays'
        ? options.skipHolidays
        : entry.control === 'mothersDay'
          ? options.skipMothersDay
          : options.skipFathersDay;
      return enabled && holidayDate(entry, date.getFullYear()) === dateValue;
    });
    const reason = custom.has(dateValue)
      ? 'custom'
      : enabledHoliday?.control === 'mothersDay'
        ? 'mothersDay'
        : enabledHoliday?.control === 'fathersDay'
          ? 'fathersDay'
          : enabledHoliday
            ? 'holiday'
            : options.skipFifthOccurrence && Math.floor((date.getDate() - 1) / 7) + 1 === 5
              ? 'fifthOccurrence'
              : null;
    if (reason) {
      skippedCounts[reason] += 1;
      skippedDates.push({ date: dateValue, reason });
    } else {
      includedDates.push(dateValue);
    }
  });
  const summary = { candidateCount: candidates.length, includedDates, skippedDates, skippedCounts };
  return includedDates.length
    ? { summary, issues: [] }
    : { summary, issues: [issue('ALL_EXCLUDED', 'All dates in this range are excluded by your current skip settings.')] };
};

export const buildWeeklySeriesPreview = (options) => {
  const { summary, issues } = planCandidates(options);
  if (issues.length) return { events: [], summary, issues };
  let seriesNumber = 0;
  const events = summary.includedDates.map((date, index) => {
    const local = createLocalEvent();
    const isLaunch = Boolean(options.launchDate) && date === options.launchDate;
    if (!isLaunch) seriesNumber += 1;
    const theme = options.assignThemes && !isLaunch ? getSundayTheme(date) : null;
    return draftToEvent({
      ...(theme ? { theme } : {}),
      event_id: `${local.event_id}_series_${index + 1}`,
      title: isLaunch ? LAUNCH_CALL_TITLE : String(options.titlePattern).replace(/\{n\}/g, String(seriesNumber)),
      date,
      start_time: options.startTime,
      end_time: options.endTime || '',
      location_label: options.locationLabel || '',
      link: options.link || '',
      description: options.description || '',
    });
  });
  return { events, summary, issues: [] };
};

export const generateWeeklySeries = (options) => buildWeeklySeriesPreview(options).events;
